import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTable, useSortBy, usePagination, useFilters } from 'react-table'
import { matchSorter } from 'match-sorter'
import {
  Box,
  Input,
  Table,
  TableRow,
  TableHeader,
  TableBody,
  TableHead,
  TableData
} from '@maui/elements'
import { SortingToggle } from './SortingToggle'
import debounce from 'lodash/debounce'

const debouncedSetFilter = debounce((value, setter) => setter(value), 300)

// https://react-table-v7.tanstack.com/docs/examples/kitchen-sink
// Define a default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, setFilter },
}) {
  const [inputValue, setInputValue] = useState(filterValue)

  const handleChange = (e) => {
    const value = e.target.value
    setInputValue(value)
    debouncedSetFilter(value, setFilter)
  }

  return (
    <Input
      value={inputValue}
      width="100%"
      onChange={(e) => handleChange(e)}
    />
  )
}

// https://react-table-v7.tanstack.com/docs/examples/kitchen-sink
export const useDefaultColumn = () => {
  return useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  )
}

// https://react-table-v7.tanstack.com/docs/examples/kitchen-sink
function textFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

// Let the table remove the filter if the string is empty
textFilterFn.autoRemove = val => !val

// https://react-table-v7.tanstack.com/docs/examples/kitchen-sink
export const useFilterTypes = () => {
  return useMemo(() => ({
    // Add a new textFilterFn filter type.
    textFilter: textFilterFn,
    // Or, override the default text filter to use
    // "startWith"
    text: (rows, id, filterValue) => {
      return rows.filter(row => {
        const rowValue = row.values[id]
        return rowValue !== undefined
          ? String(rowValue)
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase())
          : true
      })
    },
  }),
  []
  )
}

export { useTable, useSortBy, usePagination, useFilters }

export const FilterSortTable = ({
  getTableProps,
  headers,
  getTableBodyProps,
  page,
  prepareRow,
  customCell,
}) => {
  const theadProps = {
    position: 'sticky',
    top: '0',
    textAlign: 'left',
    fontSize: '12px',
    lineHeight: '16px',
    verticalAlign: 'top',
  }

  const canFilter = useMemo(() => headers.some(header => header.canFilter), [headers])
  return (
    <>
      <Table {...getTableProps()} tableLayout="fixed" width='auto' minWidth='100%'>
        <TableHead>
          <TableRow>
            {headers.map((column, i) => (
              <TableHeader key={i}
                {...column.getHeaderProps(column.getSortByToggleProps({
                  style: {
                    width: column.width,
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                  },
                }))}
                {...theadProps}
              >
                <SortingToggle
                  columnName={column.Header}
                  canSort={column.canSort}
                  isActive={column.isSorted}
                  isSortedDesc={column.isSortedDesc}
                >
                  {column.render('Header')}
                </SortingToggle>
              </TableHeader>
            ))}
          </TableRow>
          {
            canFilter && <TableRow>

              {headers.map((column, i) => (
                <TableHeader key={i} {...column.getHeaderProps()} >
                  <Box key={`${i}-${i}`} width="100%">
                    {/* Render the columns filter UI */}
                    {column.canFilter ? column.render('Filter') : null}
                  </Box>
                </TableHeader>
              ))}

            </TableRow>
          }
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {
            page.map((row, i) => {
              prepareRow(row)
              return (
                <TableRow key={i} {...row.getRowProps()} >
                  {row.cells.map((cell, j) => {
                    return (
                      <TableData
                        key={`${i}-${j}`}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        {...cell.getCellProps({
                          style: {
                            width: cell.column.width,
                            minWidth: cell.column.minWidth,
                            maxWidth: cell.column.maxWidth,
                            wordBreak: 'break-all',
                            ...cell.column.style,
                          }
                        })}
                      >
                        {customCell ? customCell(cell) : cell.render('Cell')}
                      </TableData>
                    )
                  })}
                </TableRow>
              )
            })
          }
        </TableBody>
      </Table>
    </>
  )
}

FilterSortTable.propTypes = {
  getTableProps: PropTypes.func,
  headers: PropTypes.array,
  getTableBodyProps: PropTypes.func,
  page: PropTypes.array,
  prepareRow: PropTypes.func,
  customCell: PropTypes.func,
}

export default React.memo(FilterSortTable)
